
import React, { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Card from "@mui/material/Card";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import Typography from "@mui/material/Typography";
import Snackbar from "@mui/material/Snackbar";
import Grow from "@mui/material/Grow";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import ProfileSearch from "../../../../social-stash/components/toolbar/ProfileSearch";
import SocialCard from "examples/Cards/SocialCard";

import WorkspaceService from "services/workspace-service";
import ProfileService from "services/profile-service";
// We might need a generic connect modal, for now reusing SearchCompetitorModal or similar if needed, 
// strictly speaking user just wants selection from existing. "Ask User to select profiles".
// But typically adding is also allowed. I'll include the UI for it but maybe not specific modal if not sure.
// Using SearchCompetitorModal for now as it adds profiles.
import SearchCompetitorModal from "../../SocialSnapshot/SearchCompetitorModal";
// For now let's reuse what SelectSecondaryProfile uses.
import ConnectOwnProfile from "layouts/dashboard/components/ConnectOwnProfile";
import UpgradePopup from "layouts/components/Upgrade";
import LiveDemoRestriction from "layouts/components/Upgrade/liveDemo";

const CustomTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: 400,
        textAlign: "left",
    },
});

const tooltipContent = (
    <div style={{ maxWidth: "350px", textAlign: "left" }}>
        <Typography variant="body2" gutterBottom>
            <strong>Select Profiles for Report:</strong>
        </Typography>
        <Typography variant="caption" display="block" gutterBottom>
            - Select up to 4 profiles to include in your report. <br />
            - You can mix owned and competitor profiles.
        </Typography>
    </div>
);

function Step1ProfileSelection({
    selectedProfiles = [],
    onSelect,
    maxSelection = 4,
    disabled = false,
}) {
    const [allProfiles, setAllProfiles] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(true);
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "" });

    // [NEW] Add Own Profile State
    const [showConnectOwnProfile, setShowConnectOwnProfile] = useState(false);
    const [usageRaw, setUsageRaw] = useState(null);
    const [showUpgrade, setShowUpgrade] = useState(false);
    const [showDemoPopup, setShowDemoPopup] = useState(false);

    const [toolbarTop, setToolbarTop] = useState(120);
    const [isTransparent, setIsTransparent] = useState(true);

    useEffect(() => {
        const updateToolbarTop = () => {
            const navbar = document.querySelector("header.MuiAppBar-root");
            const stepper = document.querySelector(".MuiStepper-root");
            if (navbar && stepper) {
                const totalTop = navbar.offsetHeight + stepper.offsetHeight + 8;
                setToolbarTop(totalTop);
            } else if (navbar) {
                setToolbarTop(navbar.offsetHeight + 60);
            }
        };

        updateToolbarTop();
        const resizeObserver = new ResizeObserver(updateToolbarTop);
        resizeObserver.observe(document.body);

        const scrollHandler = () => setIsTransparent(window.scrollY === 0);
        window.addEventListener("resize", updateToolbarTop);
        window.addEventListener("scroll", scrollHandler);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener("resize", updateToolbarTop);
            window.removeEventListener("scroll", scrollHandler);
        };
    }, []);

    const fetchProfiles = async () => {
        try {
            setLoading(true);
            const workspaceProfiles = await WorkspaceService.fetchWorkspaceProfiles();

            // We want ALL profiles.

            const createdAtMap = {};
            workspaceProfiles.forEach((profile) => {
                createdAtMap[profile.id] = profile.createdAt;
            });

            const grouped = workspaceProfiles.reduce((acc, profile) => {
                if (!acc[profile.platform]) acc[profile.platform] = [];
                acc[profile.platform].push(profile.id);
                return acc;
            }, {});

            let profiles = [];

            for (const platform of Object.keys(grouped)) {
                const ids = grouped[platform];
                if (ids.length > 0) {
                    const detailed = await ProfileService.fetchProfile(platform, ids).catch(() => []);
                    profiles.push(
                        ...detailed.map((p) => ({
                            ...p,
                            platform,
                            createdAt: createdAtMap[p.id] ?? null,
                            // Typically UserService profiles have 'own' flag, detailed might not.
                            // Let's re-attach it if needed, but SocialCard mostly needs name/pic/etc.
                            own: workspaceProfiles.find(up => up.id === p.id)?.own || false
                        }))
                    );
                }
            }

            profiles.sort((a, b) => {
                if (a.own === b.own) {
                    return new Date(b.createdAt) - new Date(a.createdAt);
                }
                return a.own ? -1 : 1;
            });
            setAllProfiles(profiles);
        } catch (err) {
            console.error("Failed to fetch profiles:", err);
            setAllProfiles([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProfiles();
    }, []);

    // [NEW] Fetch Usage Logic
    useEffect(() => {
        const fetchUsageData = async () => {
            try {
                const entitlements = await WorkspaceService.fetchWorkspaceEntitlements();
                if (!entitlements) return;

                setUsageRaw({
                    profilesUsed: entitlements.usage?.profiles || 0,
                    profilesLimit: entitlements.limits?.profiles || 0,
                    currentPlan: entitlements.plan || "Basic"
                });
            } catch (error) {
                console.error("Error fetching usage details:", error);
            }
        };
        fetchUsageData();
    }, []);

    const filteredProfiles = allProfiles
        .filter((profile) =>
            (profile.name || "").toLowerCase().includes((searchQuery || "").toLowerCase())
        );

    const isSelected = (id) => selectedProfiles.some((p) => p.id === id);

    const toggleSelection = (profile) => {
        if (disabled) {
            setSnackbar({ open: true, message: "⚠️ Profile selection is locked when editing a report" });
            return;
        }

        const alreadySelected = isSelected(profile.id);
        const selectedCount = selectedProfiles.length;

        if (alreadySelected) {
            onSelect(selectedProfiles.filter((p) => p.id !== profile.id));
        } else {
            if (selectedCount >= maxSelection) {
                setSnackbar({ open: false, message: "" });
                setTimeout(() => {
                    setSnackbar({ open: true, message: `⚠️ You can select up to ${maxSelection} profiles only` });
                }, 100);
                return;
            }
            onSelect([...selectedProfiles, profile]);
        }
    };



    // [NEW] Handlers for Own Profile
    const handleAddOwnProfileClick = () => {
        // Live Demo Restriction
        const isDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";
        if (isDemo) {
            setShowDemoPopup(true);
            return;
        }

        // Check Usage Limit
        if (usageRaw) {
            const { profilesUsed, profilesLimit } = usageRaw;
            if (Number(profilesUsed) >= Number(profilesLimit)) {
                setShowUpgrade(true);
                return;
            }
        }
        setShowConnectOwnProfile(true);
    };

    const handleCloseConnectOwnProfile = () => {
        setShowConnectOwnProfile(false);
        fetchProfiles(); // Refresh list
        setSnackbar({ open: true, message: "✅ Profiles refreshed" });
    };

    const handleAddCompetitorProfileClick = () => setShowSearchModal(true);

    const handleCloseSearchModal = () => {
        setShowSearchModal(false);
        fetchProfiles();
        setSnackbar({ open: true, message: "✅ Profiles refreshed" });
    };

    return (
        <MDBox>
            {disabled && (
                <MDBox mb={3} p={2} bgcolor="#e3f2fd" borderRadius="8px" border="1px solid #90caf9">
                    <MDTypography variant="body2" color="info" fontWeight="medium">
                        ℹ️ Profile selection cannot be changed when editing a report. To change profiles, please create a new report.
                    </MDTypography>
                </MDBox>
            )}
            <MDBox
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                sx={{
                    position: "sticky",
                    top: `${toolbarTop}px`,
                    zIndex: 999,
                    backgroundColor: "#ffffff",
                    padding: "5px",
                    borderRadius: "8px",
                    gap: 2,
                    mb: 5,
                    boxShadow: isTransparent ? "none" : "0 4px 12px rgba(0, 0, 0, 0.2)",
                    transition: "top 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease",
                }}
            >
                <MDBox flexGrow={1}>
                    <ProfileSearch searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
                </MDBox>

                <MDBox display="flex" flexDirection="column" alignItems="center">
                    <MDTypography
                        variant="button"
                        fontWeight="medium"
                        color="text"
                        sx={{ marginBottom: 1, display: "block", textAlign: "center" }}
                    >
                        Connect
                    </MDTypography>

                    <MDBox
                        sx={{
                            borderRadius: "16px",
                            backgroundColor: "#f0f2f5",
                            padding: "14px",
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            gap: 2,
                            flexWrap: "wrap",
                        }}
                    >

                        {/* 🟢 Add Own Profile */}
                        <Card
                            onClick={disabled ? undefined : handleAddOwnProfileClick}
                            sx={{
                                cursor: disabled ? "not-allowed" : "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexDirection: "row",
                                gap: 1,
                                padding: "4px 18px",
                                borderRadius: "12px",
                                backgroundColor: "grey.100",
                                boxShadow: 1,
                                border: "1px solid #ddd",
                                transition: "all 0.3s ease-in-out",
                                opacity: disabled ? 0.5 : 1,
                                "&:hover": disabled ? {
                                    transform: "none",
                                    boxShadow: 1,
                                } : {
                                    transform: "translateY(-4px)",
                                    boxShadow: 4,
                                },
                                "&:active": disabled ? {} : {
                                    backgroundColor: "grey.400",
                                    boxShadow: 6,
                                },
                            }}
                        >
                            <IconButton color="primary" sx={{ p: 0 }}>
                                <AddCircleIcon fontSize="large" />
                            </IconButton>
                            <Typography variant="subtitle2" fontWeight="bold" color="textPrimary" ml={1}>
                                Add Own Profile
                            </Typography>
                        </Card>

                        {/* 🔵 Add Competitor Profile */}
                        <Card
                            onClick={disabled ? undefined : handleAddCompetitorProfileClick}
                            sx={{
                                cursor: disabled ? "not-allowed" : "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexDirection: "row",
                                gap: 1,
                                padding: "4px 18px",
                                borderRadius: "12px",
                                backgroundColor: "grey.100",
                                boxShadow: 1,
                                border: "1px solid #ddd",
                                transition: "all 0.3s ease-in-out",
                                opacity: disabled ? 0.5 : 1,
                                "&:hover": disabled ? {
                                    transform: "none",
                                    boxShadow: 1,
                                } : {
                                    transform: "translateY(-4px)",
                                    boxShadow: 4,
                                },
                                "&:active": disabled ? {} : {
                                    backgroundColor: "grey.400",
                                    boxShadow: 6,
                                },
                            }}
                        >
                            <IconButton color="primary" sx={{ p: 0 }}>
                                <AddCircleIcon fontSize="large" />
                            </IconButton>
                            <Typography variant="subtitle2" fontWeight="bold" color="textPrimary" ml={1}>
                                Add Competitor Profile
                            </Typography>
                        </Card>
                    </MDBox>
                </MDBox>
            </MDBox>

            <Card>
                <MDBox>
                    <MDBox
                        mx={2}
                        mt={-3}
                        py={1}
                        px={2}
                        variant="gradient"
                        bgColor="info"
                        borderRadius="lg"
                        coloredShadow="info"
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                    >
                        <MDTypography variant="h6" color="white">
                            Select Profiles ({selectedProfiles.length}/{maxSelection})
                        </MDTypography>
                        <CustomTooltip title={tooltipContent} placement="bottom" arrow>
                            <IconButton sx={{ color: "white", p: 0, ml: 0.5 }}>
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                        </CustomTooltip>
                    </MDBox>

                    <MDBox px={2} py={3}>
                        {loading ? (
                            <MDBox display="flex" justifyContent="center" alignItems="center" p={3}>
                                <CircularProgress color="info" />
                            </MDBox>
                        ) : filteredProfiles.length === 0 ? (
                            <MDTypography variant="subtitle1" color="text" align="center">
                                No profiles found.
                            </MDTypography>
                        ) : (
                            <MDBox
                                display="flex"
                                flexWrap="wrap"
                                justifyContent="center"
                                gap={2}
                                px={2}
                                mt={0}
                            >
                                {filteredProfiles.map((profile, index) => {
                                    return (
                                        <Grow
                                            in={true}
                                            timeout={index * 50}
                                            key={profile.id}
                                            style={{ transformOrigin: "0 0 0" }}
                                        >
                                            <MDBox
                                                key={profile.id}
                                                sx={{
                                                    flex: "1 1 calc(25% - 16px)",
                                                    minWidth: "240px",
                                                    maxWidth: "300px",
                                                    opacity:
                                                        selectedProfiles.length >= maxSelection && !isSelected(profile.id) ? 0.6 : 1,
                                                    pointerEvents: "auto",
                                                    transition: "opacity 0.3s ease",
                                                }}
                                            >
                                                <SocialCard
                                                    platform={profile.platform}
                                                    profilePic={profile.profilePictureUrl || ""}
                                                    name={profile.name || "Untitled"}
                                                    count={profile.count ?? 0}
                                                    link={profile.link}
                                                    verifiedStatus={profile.verifiedStatus ?? false}
                                                    updated_at={profile.updated_at}
                                                    isSelected={isSelected(profile.id)}
                                                    onSelect={() => toggleSelection(profile)}
                                                    isOwn={profile.own}
                                                />
                                            </MDBox>
                                        </Grow>
                                    );
                                })}
                            </MDBox>
                        )}
                    </MDBox>
                </MDBox>
            </Card>

            <SearchCompetitorModal open={showSearchModal} onClose={handleCloseSearchModal} />

            {/* [NEW] Connect Own Profile Modal */}
            {showConnectOwnProfile && (
                <ConnectOwnProfile
                    open={showConnectOwnProfile}
                    onClose={handleCloseConnectOwnProfile}
                />
            )}

            {/* [NEW] Upgrade Popup */}
            <UpgradePopup
                open={showUpgrade}
                onClose={() => setShowUpgrade(false)}
                type="usage"
                featureName="Tracked Profiles"
                planRequired="Higher Plan"
                currentPlan={usageRaw?.currentPlan}
                usageLimit={usageRaw?.profilesLimit}
            />

            {/* [NEW] Live Demo Restriction Popup */}
            <LiveDemoRestriction
                open={showDemoPopup}
                onClose={() => setShowDemoPopup(false)}
                featureName="Connecting Your Profile"
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                ContentProps={{
                    sx: { borderRadius: 2, boxShadow: 3 },
                }}
            />
        </MDBox>
    );
}

export default Step1ProfileSelection;
